import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Plus, Pencil, Trash2, Building2, User, FolderOpen, Loader2, Briefcase } from 'lucide-react';
import ProjectForm from './ProjectForm';
import RichTextDisplay from '@/components/ui/rich-text-display';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ProjectManager {
  id: string;
  name: string;
  designation?: string | null;
  email?: string | null;
  phone?: string | null;
}

interface Project {
  id: string;
  name: string;
  description?: string | null;
  tech_stack?: string[] | null;
  start_date?: string | null;
  end_date?: string | null;
  client_company_id?: string | null;
  client_manager_id?: string | null;
  experience_id?: string | null;
  client_company?: { id: string; name: string } | null;
  client_manager?: ProjectManager | null;
  experience?: { id: string; job_title: string; companies: { name: string } | null } | null;
  all_managers?: ProjectManager[];
}

interface ProjectsTabProps {
  candidateId: string;
}

interface ProjectsTabProps {
  candidateId: string;
}

const ProjectsTab = ({ candidateId }: ProjectsTabProps) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchProjects();
  }, [candidateId]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('candidate_projects')
        .select(`
          *,
          client_company:client_company_id (id, name),
          client_manager:client_manager_id (id, name, designation, email, phone),
          experience:experience_id (id, job_title, companies:company_id (name))
        `)
        .eq('candidate_id', candidateId)
        .order('start_date', { ascending: false });

      if (error) throw error;
      
      // Transform the data and fetch all managers for each client company
      const transformed = await Promise.all((data || []).map(async (p) => {
        let allManagers: ProjectManager[] = [];
        
        // Fetch all managers from the client company if it exists
        if (p.client_company_id) {
          const { data: managersData } = await supabase
            .from('company_people')
            .select('id, name, designation, email, phone')
            .eq('company_id', p.client_company_id)
            .order('name');
          allManagers = managersData || [];
        }
        
        return {
          ...p,
          experience: p.experience ? {
            id: (p.experience as any).id,
            job_title: (p.experience as any).job_title,
            companies: Array.isArray((p.experience as any).companies) 
              ? (p.experience as any).companies[0] || null 
              : (p.experience as any).companies
          } : null,
          all_managers: allManagers
        };
      }));
      
      setProjects(transformed as Project[]);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (project: Project) => {
    setEditingProject(project);
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (!projectToDelete) return;
    
    try {
      const { error } = await supabase
        .from('candidate_projects')
        .delete()
        .eq('id', projectToDelete);

      if (error) throw error;
      toast({ title: 'Project deleted successfully' });
      fetchProjects();
    } catch (error: any) {
      toast({ title: 'Error deleting project', description: error.message, variant: 'destructive' });
    } finally {
      setDeleteConfirmOpen(false);
      setProjectToDelete(null);
    }
  };

  const openAddForm = () => {
    setEditingProject(null);
    setFormOpen(true);
  };

  // Group projects by client company
  const groupedProjects = projects.reduce((acc, project) => {
    const companyName = project.client_company?.name || 'Unassigned';
    const companyId = project.client_company?.id || 'unassigned';
    
    if (!acc[companyId]) {
      acc[companyId] = {
        name: companyName,
        projects: []
      };
    }
    acc[companyId].projects.push(project);
    return acc;
  }, {} as Record<string, { name: string; projects: Project[] }>);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Projects</h3>
        <Button size="sm" onClick={openAddForm}>
          <Plus className="h-4 w-4 mr-1" />
          Add Project
        </Button>
      </div>

      {projects.length === 0 ? (
        <Card className="p-8 text-center">
          <FolderOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No projects added yet</p>
          <Button variant="outline" className="mt-4" onClick={openAddForm}>
            <Plus className="h-4 w-4 mr-1" />
            Add First Project
          </Button>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedProjects).map(([companyId, group]) => (
            <div key={companyId}>
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="h-4 w-4 text-primary" />
                <h4 className="font-medium text-sm">{group.name}</h4>
                <Badge variant="outline" className="text-xs">
                  {group.projects.length} project{group.projects.length !== 1 ? 's' : ''}
                </Badge>
              </div>
              
              <div className="space-y-3 pl-6 border-l-2 border-muted">
                {group.projects.map((project) => (
                  <Card key={project.id} className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <h5 className="font-semibold">{project.name}</h5>
                        {(project.start_date || project.end_date) && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {project.start_date} - {project.end_date || 'Present'}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(project)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => {
                            setProjectToDelete(project.id);
                            setDeleteConfirmOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {project.description && (
                      <RichTextDisplay content={project.description} className="mt-2" />
                    )}

                    {project.tech_stack && project.tech_stack.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {project.tech_stack.map((tech, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {tech}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {project.experience && (
                      <div className="mt-3 pt-3 border-t flex items-center gap-2 text-sm">
                        <Briefcase className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Linked to:</span>
                        <span className="font-medium">{project.experience.job_title}</span>
                        {project.experience.companies?.name && (
                          <span className="text-muted-foreground">at {project.experience.companies.name}</span>
                        )}
                      </div>
                    )}

                    {project.all_managers && project.all_managers.length > 0 && (
                      <div className="mt-3 pt-3 border-t space-y-2">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <User className="h-4 w-4" />
                          <span>Managers ({project.all_managers.length})</span>
                        </div>
                        <div className="grid gap-2 pl-6">
                          {project.all_managers.map((manager) => (
                            <div key={manager.id} className="flex items-center gap-2 text-sm">
                              <span className="font-medium">{manager.name}</span>
                              {manager.designation && (
                                <span className="text-muted-foreground">({manager.designation})</span>
                              )}
                              {manager.email && (
                                <a href={`mailto:${manager.email}`} className="text-primary hover:underline ml-auto">
                                  {manager.email}
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <ProjectForm
        open={formOpen}
        onOpenChange={setFormOpen}
        candidateId={candidateId}
        project={editingProject}
        onSaved={fetchProjects}
      />

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this project? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProjectsTab;
